// =============================================================================
// RH › Pesquisa de Escala — formulário público (o colaborador, sem login)
// =============================================================================
// Rota: /pesquisaescala/:token   ·   Card: docs/PLANO.md › Trilho B
//
// ⚠️ AS PERGUNTAS NÃO MORAM AQUI. Esta tela pede o questionário à API
// (`GET /api/rh/pesquisa-escala/public/questionario/:token`) e desenha o que
// vier. A fonte única é server/rh/pesquisa-escala-perguntas.js.
// Foi decisão explícita: a Auditoria SA e a Avaliação TSV duplicaram os pesos das
// perguntas entre back e front, e as duas cópias divergiram. Aqui não existe um
// segundo lugar para esquecer de atualizar.
//
// ⚠️ O link chega por WhatsApp — quase todo mundo responde pelo CELULAR. Por isso
// o layout é de coluna única e os botões de opção são grandes o bastante para o
// polegar (mínimo 48px de altura, recomendação de acessibilidade de toque).

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

// Cor do módulo RH (ver docs/ARQUITETURA.md › Paleta dos módulos).
const COR = '#e11d48';

/**
 * @param {boolean} previa  Modo PRÉVIA: o RH vendo o formulário sem token.
 *
 * ⚠️ A prévia usa ESTE MESMO componente de propósito — é o único jeito de
 * garantir que o que o RH confere é exatamente o que o colaborador recebe. Uma
 * tela de prévia separada seria uma segunda cópia do visual, que divergiria na
 * primeira alteração de layout e passaria a mostrar uma pesquisa que não existe.
 * O que muda na prévia: o questionário vem da rota AUTENTICADA (sem token) e o
 * envio é bloqueado — dá para marcar as opções, mas nada é gravado.
 */
function PesquisaEscalaExterno({ previa = false }) {
    const { token } = useParams();
    const [perguntas, setPerguntas] = useState([]);
    // O que cada escala significa, em português de loja. Vem da API junto com as
    // perguntas — mesma fonte única, para a explicação nunca divergir do que está
    // sendo perguntado.
    const [escalas, setEscalas] = useState([]);
    const [respostas, setRespostas] = useState({});
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState('');
    const [enviada, setEnviada] = useState(false);

    useEffect(() => {
        const carregar = async () => {
            try {
                const [url, cabecalho] = previa
                    ? ['/api/rh/pesquisa-escala/questionario', { Authorization: `Bearer ${localStorage.getItem('authToken')}` }]
                    : [`/api/rh/pesquisa-escala/public/questionario/${token}`, {}];
                const resposta = await fetch(url, { headers: cabecalho });
                const dados = await resposta.json();
                if (!resposta.ok) throw new Error(dados.message || 'Não foi possível abrir a pesquisa.');
                setPerguntas(dados.perguntas || []);
                setEscalas(dados.escalas || []);
            } catch (e) {
                setErro(e.message);
            } finally {
                setCarregando(false);
            }
        };
        carregar();
    }, [token, previa]);

    const responder = (id, valor) => setRespostas((atual) => ({ ...atual, [id]: valor }));

    const enviar = async (evento) => {
        evento.preventDefault();
        // Trava dupla: o botão já fica desabilitado na prévia, mas um Enter num
        // campo também submete o formulário — e aí gravaria resposta de mentira.
        if (previa) return;
        setEnviando(true);
        try {
            const resposta = await fetch('/api/rh/pesquisa-escala/public/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, respostas }),
            });
            const dados = await resposta.json();
            if (!resposta.ok) throw new Error(dados.message || 'Não foi possível registrar sua resposta.');
            setEnviada(true);
        } catch (e) {
            // ⚠️ Sem `alert`: no celular ele some atrás do teclado e a pessoa acha
            // que travou. O aviso fica na própria tela, acima do botão.
            setErro(e.message);
            setEnviando(false);
        }
    };

    // Explicação de uma opção, quando ela é uma das escalas ("12x36" → "trabalha
    // um dia e folga no outro"). Devolve null para as opções neutras.
    const explicacaoDa = (opcao) => escalas.find((e) => e.nome === opcao)?.explicacao || null;

    const respondidas = perguntas.filter((p) => respostas[p.id]).length;
    const total = perguntas.length;
    const completo = total > 0 && respondidas === total;
    const progresso = total > 0 ? Math.round((respondidas / total) * 100) : 0;

    const cartao = {
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        padding: '20px',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    };

    const telaCheia = (fundo, cor) => ({
        display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
        minHeight: '100vh', backgroundColor: fundo, color: cor, padding: '24px', textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
    });

    if (carregando) {
        return <div style={telaCheia('#f8fafc', '#64748b')}><p style={{ fontSize: '17px' }}>Carregando a pesquisa…</p></div>;
    }

    // Erro de ABERTURA (link inválido/expirado/já respondido) ocupa a tela toda —
    // não há o que fazer nela. Erro de ENVIO é tratado dentro do formulário, para
    // a pessoa não perder o que já preencheu.
    if (erro && !perguntas.length) {
        return (
            <div style={telaCheia('#fef2f2', '#991b1b')}>
                <div style={{ fontSize: '56px', marginBottom: '12px' }}>⚠️</div>
                <h2 style={{ fontSize: '21px', marginBottom: '10px' }}>Não foi possível abrir a pesquisa</h2>
                <p style={{ maxWidth: '420px', lineHeight: 1.6 }}>{erro}</p>
            </div>
        );
    }

    if (enviada) {
        return (
            <div style={telaCheia('#ecfdf5', '#065f46')}>
                <div style={{ fontSize: '64px', marginBottom: '12px' }}>✅</div>
                <h2 style={{ fontSize: '23px', marginBottom: '10px' }}>Resposta registrada!</h2>
                <p style={{ maxWidth: '420px', lineHeight: 1.6, color: '#047857' }}>
                    Obrigado por participar. Sua opinião vai ajudar a empresa a decidir sobre as escalas de trabalho.
                </p>
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: '#f1f5f9', minHeight: '100vh', padding: '28px 16px 60px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            <div style={{ width: '100%', maxWidth: '640px', margin: '0 auto' }}>

                {previa && (
                    <div style={{
                        backgroundColor: '#1e293b', color: '#e2e8f0', borderRadius: '10px',
                        padding: '12px 16px', marginBottom: '18px', fontSize: '13px', lineHeight: 1.6,
                    }}>
                        <strong>Prévia</strong> — é exatamente esta tela que o colaborador recebe pelo WhatsApp.
                        Pode marcar as opções à vontade: <strong>nada é gravado</strong> e o envio está desligado.
                    </div>
                )}

                <div style={{ textAlign: 'center', marginBottom: '22px' }}>
                    <img alt="Grupo Correia" src="/icon-logotipo.png"
                        style={{ height: '96px', maxWidth: '100%', objectFit: 'contain' }} />
                </div>

                <div style={{ ...cartao, borderLeft: `4px solid ${COR}`, backgroundColor: '#fff1f2' }}>
                    <h1 style={{ fontSize: '19px', fontWeight: 'bold', color: '#9f1239', margin: '0 0 12px 0' }}>
                        Pesquisa de Escala de Trabalho
                    </h1>
                    <div style={{ color: '#881337', fontSize: '14px', lineHeight: 1.7 }}>
                        <p style={{ margin: '0 0 10px 0' }}>
                            Queremos entender qual escala de trabalho funciona melhor para você — <strong>12x36</strong> ou <strong>5x2</strong>.
                        </p>
                        <ul style={{ paddingLeft: '20px', margin: '0 0 10px 0' }}>
                            {/* ⚠️ O texto fala em "sem identificação individual" e NÃO em
                                "100% anônima" (como faz a Pesquisa de Clima). O vínculo
                                pessoa↔resposta é recomponível por quem tem o banco e a
                                chave do sistema — prometer anonimato absoluto seria falso. */}
                            <li><strong>Sem identificação individual:</strong> os resultados são lidos por grupo, nunca pessoa a pessoa.</li>
                            <li><strong>Não existe resposta certa:</strong> responda pensando na sua rotina de verdade.</li>
                            <li><strong>Responder não muda sua escala:</strong> esta pesquisa é para ouvir a equipe antes de qualquer decisão.</li>
                        </ul>
                        <p style={{ margin: 0, borderTop: '1px solid #fecdd3', paddingTop: '10px' }}>
                            São {total} perguntas rápidas e o link só pode ser respondido uma vez.
                        </p>
                    </div>
                </div>

                {/* LEGENDA DAS ESCALAS — em cartão próprio e antes das perguntas.
                    ⚠️ Não é decoração: "12x36" e "5x2" são nomes de contracheque, e
                    parte da operação não liga o nome ao próprio ritmo de trabalho.
                    Quem não entende o nome responde no chute, e a pesquisa inteira
                    perde o sentido. Por isso a explicação aparece DUAS vezes: aqui,
                    e de novo embaixo de cada opção — a pessoa não precisa rolar de
                    volta ao topo na sétima pergunta. */}
                {escalas.length > 0 && (
                    <div style={{ ...cartao, padding: '18px 20px' }}>
                        <p style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
                            O que quer dizer cada escala
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {escalas.map((escala) => (
                                <div key={escala.nome} style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                                    <span style={{
                                        backgroundColor: COR, color: '#fff', borderRadius: '6px',
                                        padding: '3px 10px', fontSize: '13.5px', fontWeight: 700,
                                        whiteSpace: 'nowrap', flexShrink: 0,
                                    }}>
                                        {escala.nome}
                                    </span>
                                    <span style={{ fontSize: '14px', color: '#334155', lineHeight: 1.5 }}>
                                        {escala.explicacao}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div style={{ ...cartao, padding: '14px 18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: '#64748b' }}>
                        <span>Progresso</span>
                        <span style={{ fontWeight: 'bold', color: completo ? '#059669' : '#475569', fontVariantNumeric: 'tabular-nums' }}>
                            {respondidas} de {total}
                        </span>
                    </div>
                    <div style={{ backgroundColor: '#e2e8f0', borderRadius: '99px', height: '10px', overflow: 'hidden' }}>
                        <div style={{
                            width: `${progresso}%`, height: '100%',
                            backgroundColor: completo ? '#10b981' : COR,
                            borderRadius: '99px', transition: 'width 0.3s ease',
                        }} />
                    </div>
                </div>

                <form onSubmit={enviar}>
                    {perguntas.map((pergunta, indice) => {
                        const marcada = respostas[pergunta.id];
                        // A P_8 só faz sentido para quem respondeu "Sim" na P_7. Ela
                        // continua obrigatória (a opção "Não se aplica" é a saída de
                        // quem não quer mudar) — o que muda aqui é só a ajuda visual.
                        const dependeAtendida = !pergunta.depende
                            || respostas[pergunta.depende.pergunta] === pergunta.depende.valor;

                        return (
                            <div key={pergunta.id} style={cartao}>
                                <p style={{ fontSize: '15px', fontWeight: 600, color: '#334155', margin: '0 0 4px 0', lineHeight: 1.5 }}>
                                    <span style={{ color: COR, marginRight: '6px' }}>{indice + 1}.</span>
                                    {pergunta.texto}
                                </p>

                                {pergunta.depende && !dependeAtendida && (
                                    <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: '0 0 12px 0', lineHeight: 1.5 }}>
                                        Você respondeu que não quer mudar — marque “Não se aplica”.
                                    </p>
                                )}
                                {(!pergunta.depende || dependeAtendida) && <div style={{ height: '12px' }} />}

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {pergunta.opcoes.map((opcao) => {
                                        const selecionada = marcada === opcao;
                                        const idRadio = `${pergunta.id}-${opcao}`;
                                        const explicacao = explicacaoDa(opcao);
                                        return (
                                            <React.Fragment key={opcao}>
                                                <input
                                                    type="radio" id={idRadio} name={pergunta.id} value={opcao}
                                                    checked={!!selecionada}
                                                    onChange={() => responder(pergunta.id, opcao)}
                                                    style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                                                />
                                                <label
                                                    htmlFor={idRadio}
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '10px',
                                                        // 48px é o alvo mínimo de toque — o link chega por
                                                        // WhatsApp e é respondido no celular.
                                                        minHeight: '48px', padding: '10px 14px',
                                                        fontSize: '15px', lineHeight: 1.35,
                                                        backgroundColor: selecionada ? COR : '#f8fafc',
                                                        color: selecionada ? '#fff' : '#334155',
                                                        border: `2px solid ${selecionada ? COR : '#e2e8f0'}`,
                                                        borderRadius: '10px', cursor: 'pointer',
                                                        fontWeight: selecionada ? 600 : 400,
                                                        transition: 'all 0.15s ease',
                                                    }}
                                                >
                                                    <span style={{
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                        minWidth: '20px', width: '20px', height: '20px', borderRadius: '50%',
                                                        border: `2px solid ${selecionada ? '#fff' : '#cbd5e1'}`,
                                                        backgroundColor: 'transparent', flexShrink: 0,
                                                    }}>
                                                        {selecionada && <span style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: '#fff' }} />}
                                                    </span>
                                                    <span>
                                                        {opcao}
                                                        {/* A explicação em segunda linha, menor: lembra o que é a
                                                            escala sem competir com o nome dela. Só aparece nas
                                                            opções que SÃO escala — "Não sei" não precisa de glosa. */}
                                                        {explicacao && (
                                                            <span style={{
                                                                display: 'block', marginTop: '2px',
                                                                fontSize: '12.5px', fontWeight: 400, lineHeight: 1.35,
                                                                color: selecionada ? 'rgba(255,255,255,0.85)' : '#64748b',
                                                            }}>
                                                                {explicacao}
                                                            </span>
                                                        )}
                                                    </span>
                                                </label>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    {/* Erro de ENVIO: fica aqui, colado no botão, para a pessoa não
                        perder o preenchimento nem precisar caçar o aviso. */}
                    {erro && (
                        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', marginBottom: '14px', fontSize: '13.5px', color: '#991b1b', lineHeight: 1.5 }}>
                            {erro}
                        </div>
                    )}

                    {!completo && (
                        <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '14px', marginBottom: '14px', fontSize: '13.5px', color: '#92400e', lineHeight: 1.5 }}>
                            Faltam <strong>{total - respondidas}</strong> pergunta(s) para poder enviar.
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={previa || enviando || !completo}
                        style={{
                            width: '100%', padding: '16px',
                            backgroundColor: previa || !completo ? '#94a3b8' : (enviando ? '#e11d4899' : COR),
                            color: '#fff', fontSize: '16px', fontWeight: 'bold',
                            border: 'none', borderRadius: '10px',
                            cursor: previa || !completo || enviando ? 'not-allowed' : 'pointer',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                            transition: 'background-color 0.2s',
                        }}
                    >
                        {previa ? 'Envio desligado na prévia'
                            : enviando ? 'Enviando…'
                            : completo ? 'Enviar respostas'
                            : `Responda todas (${respondidas}/${total})`}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default PesquisaEscalaExterno;
